// Chakra imports
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Badge,
  Divider,
  Icon,
  SimpleGrid,
  Card,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Services
import studentsService from 'services/studentsService';
import { getCurrentUser } from 'services/authService';
import { USER_ROLES } from 'constants/roles';
import { formatDate } from 'utils/format';

// Icons
import { MdArrowBack, MdPerson, MdEmail, MdSchool, MdPhone, MdCalendarToday, MdLocationOn, MdLabel, MdEdit, MdDelete } from 'react-icons/md';

export default function ViewStudent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  // Chakra Color Mode
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const bgStatus = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
  const warningBg = useColorModeValue('red.50', 'red.900');
  const warningBorder = useColorModeValue('red.200', 'red.700');
  const warningText = useColorModeValue('red.600', 'red.300');

  // State
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    center: '',
    region: '',
    field: '',
    former_school: '',
    referral: '',
    application_date: '',
    concour: [],
  });
  const [concourInput, setConcourInput] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isCenterAdmin = getCurrentUser()?.role === USER_ROLES.CENTER_ADMIN;
  
  // Modals
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = useRef();

  // Fetch student data
  useEffect(() => {
    const fetchStudent = async () => {
      if (!id) {
        setError('Student ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await studentsService.getStudent(id);

        if (result.success && result.data) {
          setStudent(result.data);
        } else {
          setError(result.error || 'Failed to load student');
          toast({
            title: 'Error',
            description: result.error || 'Failed to load student',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (err) {
        console.error('Error fetching student:', err);
        setError('Failed to load student. Please try again.');
        toast({
          title: 'Error',
          description: 'Failed to load student. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStudent();
  }, [id, toast]);

  // Initialize form data when student is loaded
  useEffect(() => {
    if (student) {
      setFormData({
        name: student.name || '',
        email: student.email || '',
        contact: student.contact || '',
        center: student.center || '',
        region: student.region || '',
        field: student.field || '',
        former_school: student.former_school || '',
        referral: student.referral || '',
        application_date: student.application_date ? student.application_date.split('T')[0] : '',
        concour: student.concour || [],
      });
    }
  }, [student]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle concour addition
  const handleAddConcour = () => {
    if (concourInput.trim()) {
      setFormData(prev => ({
        ...prev,
        concour: [...prev.concour, concourInput.trim()],
      }));
      setConcourInput('');
    }
  };

  // Handle concour removal
  const handleRemoveConcour = (index) => {
    setFormData(prev => ({
      ...prev,
      concour: prev.concour.filter((_, i) => i !== index),
    }));
  };

  // Handle update student
  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const updateData = {
        ...formData,
        application_date: formData.application_date ? new Date(formData.application_date).toISOString() : undefined,
      };

      // Remove empty fields
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '' || updateData[key] === undefined || (Array.isArray(updateData[key]) && updateData[key].length === 0)) {
          delete updateData[key];
        }
      });

      if (isCenterAdmin) {
        delete updateData.center;
      }

      const result = await studentsService.updateStudentPartial(id, updateData);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Student updated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onEditClose();
        // Refresh student data
        const fetchResult = await studentsService.getStudent(id);
        if (fetchResult.success && fetchResult.data) {
          setStudent(fetchResult.data);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to update student',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Update error:', err);
      toast({
        title: 'Error',
        description: 'Failed to update student. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete student
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const result = await studentsService.deleteStudent(id);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Student deleted successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onDeleteClose();
        // Navigate back to students list
        navigate('/admin/students');
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to delete student',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete student. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Center minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Center>
      </Box>
    );
  }

  if (error || !student) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Card p='40px' bg={cardBg}>
          <Flex direction='column' align='center' gap='20px'>
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              {error || 'Student not found'}
            </Text>
            <Button onClick={() => navigate(-1)} leftIcon={<Icon as={MdArrowBack} />}>
              Go Back
            </Button>
          </Flex>
        </Card>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        {/* Back, Edit, and Delete Buttons */}
        <Flex justify='space-between' align='center' mb='10px' flexWrap='wrap' gap='10px'>
          <Button
            onClick={() => navigate(-1)}
            leftIcon={<Icon as={MdArrowBack} />}
            variant='ghost'>
            Go Back
          </Button>
          <Flex gap='10px' flexWrap='wrap'>
            <Button
              onClick={onEditOpen}
              leftIcon={<Icon as={MdEdit} />}
              colorScheme='blue'
              variant='solid'>
              Edit Student
            </Button>
            <Button
              onClick={onDeleteOpen}
              leftIcon={<Icon as={MdDelete} />}
              colorScheme='red'
              variant='solid'>
              Delete Student
            </Button>
          </Flex>
        </Flex>

        {/* Student Header */}
        <Card p='30px' bg={cardBg}>
          <Flex direction='column' gap='20px'>
            <Flex justify='space-between' align='center' flexWrap='wrap' gap='15px'>
              <Flex direction='column'>
                <Text color={textColor} fontSize='2xl' fontWeight='700' mb='5px'>
                  Student Details
                </Text>
                <Text color={textColorSecondary} fontSize='sm'>
                  Matricule: {student.matricule}
                </Text>
              </Flex>
              <Badge 
                colorScheme='brand' 
                bg={bgStatus} 
                fontSize="md" 
                fontWeight="700" 
                px="15px" 
                py="5px"
              >
                SN: {student.sn}
              </Badge>
            </Flex>
            <Divider borderColor={borderColor} />
          </Flex>
        </Card>

        {/* Personal Information */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdPerson} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Personal Information
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Full Name
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {student.name}
              </Text>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdEmail} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Email
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {student.email}
              </Text>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdPhone} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Contact
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600' fontFamily="mono">
                {student.contact}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Matricule
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600' fontFamily="mono">
                {student.matricule}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Academic Information */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdSchool} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Academic Information
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdLocationOn} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Center
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {student.center}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Region
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {student.region || 'N/A'}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Field
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {student.field}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Former School
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {student.former_school || 'N/A'}
              </Text>
            </Box>
            {student.referral && (
              <Box>
                <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                  Referral
                </Text>
                <Text color={textColor} fontSize='md' fontWeight='600' fontFamily="mono">
                  {student.referral}
                </Text>
              </Box>
            )}
          </SimpleGrid>
        </Card>

        {/* Concour Information */}
        {student.concour && student.concour.length > 0 && (
          <Card p='30px' bg={cardBg}>
            <Flex align='center' gap='10px' mb='20px'>
              <Icon as={MdLabel} color={textColor} fontSize='xl' />
              <Text color={textColor} fontSize='xl' fontWeight='600'>
                Concour Applications
              </Text>
            </Flex>
            <Flex gap='10px' flexWrap='wrap'>
              {student.concour.map((concour, index) => (
                <Badge
                  key={index}
                  colorScheme='purple'
                  bg={bgStatus}
                  fontSize="sm"
                  fontWeight="700"
                  px="12px"
                  py="5px"
                >
                  {concour}
                </Badge>
              ))}
            </Flex>
          </Card>
        )}

        {/* Timestamps */}
        <Card p='30px' bg={cardBg}>
          <Text color={textColor} fontSize='xl' fontWeight='600' mb='20px'>
            Timestamps
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdCalendarToday} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Application Date
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='500'>
                {formatDate(student.application_date)}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Created At
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='500'>
                {formatDate(student.created_at)}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Last Updated
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='500'>
                {formatDate(student.updated_at)}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>
      </Flex>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor} fontSize="2xl" fontWeight="700">
            Edit Student
          </ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody pb="24px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Full Name
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Email
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter email"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Contact
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="contact"
                  value={formData.contact}
                  onChange={handleInputChange}
                  placeholder="Enter contact number"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Center
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="center"
                  value={formData.center}
                  onChange={handleInputChange}
                  placeholder="Enter center"
                  isDisabled={isCenterAdmin}
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Region
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                  placeholder="Enter region"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Field
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="field"
                  value={formData.field}
                  onChange={handleInputChange}
                  placeholder="Enter field"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Former School
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="former_school"
                  value={formData.former_school}
                  onChange={handleInputChange}
                  placeholder="Enter former school"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Referral
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="referral"
                  value={formData.referral}
                  onChange={handleInputChange}
                  placeholder="Enter referral code"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Application Date
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="date"
                  name="application_date"
                  value={formData.application_date}
                  onChange={handleInputChange}
                  mb='8px'
                />
              </FormControl>

              <FormControl gridColumn={{ base: '1', md: '1 / 3' }}>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Concour Applications
                </FormLabel>
                <Flex gap='10px' mb='10px' flexWrap='wrap'>
                  {formData.concour.map((concour, index) => (
                    <Badge
                      key={index}
                      colorScheme='purple'
                      bg={bgStatus}
                      fontSize="sm"
                      fontWeight="700"
                      px="12px"
                      py="5px"
                      cursor="pointer"
                      onClick={() => handleRemoveConcour(index)}
                    >
                      {concour} ×
                    </Badge>
                  ))}
                </Flex>
                <Flex gap='10px'>
                  <Input
                    variant='main'
                    fontSize='sm'
                    type="text"
                    value={concourInput}
                    onChange={(e) => setConcourInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddConcour();
                      }
                    }}
                    placeholder="Add concour (press Enter)"
                    mb='8px'
                    flex={1}
                  />
                  <Button
                    onClick={handleAddConcour}
                    size="sm"
                    colorScheme="purple"
                    variant="solid">
                    Add
                  </Button>
                </Flex>
              </FormControl>
            </SimpleGrid>
          </ModalBody>

          <ModalFooter>
            <Button
              variant='light'
              fontWeight='500'
              mr={3}
              onClick={onEditClose}
              isDisabled={updating}>
              Cancel
            </Button>
            <Button
              variant='brand'
              fontWeight='500'
              onClick={handleUpdate}
              isLoading={updating}
              loadingText="Updating">
              Update Student
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardBg}>
            <AlertDialogHeader fontSize="xl" fontWeight="700" color={textColor}>
              Delete Student
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text color={textColor} fontSize="md" mb="10px">
                Are you sure you want to delete this student application? This action cannot be undone.
              </Text>
              {student && (
                <Box
                  mt="15px"
                  p="12px"
                  borderRadius="8px"
                  bg={warningBg}
                  border="1px solid"
                  borderColor={warningBorder}>
                  <Text color={warningText} fontSize="sm" fontWeight="500">
                    ⚠️ This will permanently delete the student application record.
                  </Text>
                </Box>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant='light'
                fontWeight='500'
                onClick={onDeleteClose}
                isDisabled={deleting}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                variant='solid'
                fontWeight='500'
                onClick={handleDelete}
                ml={3}
                isLoading={deleting}
                loadingText="Deleting">
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
